import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useDebounce } from '../../../hooks/useDebounce'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { useExpenseMutations } from '../hooks/useExpenseMutations'
import { useInvoiceMutations } from '../hooks/useInvoiceMutations'
import type { Reload } from '../hooks/useCostWriteQueue'
import { describeCostControlError, isRecordGoneError } from '../lib/errors'
import {
  EMPTY_LEDGER_FILTER, expenseAffordances, invoiceAffordances, invoiceContractOptions, isLedgerFiltered, ledgerQueries, ledgerRows, type LedgerFilterState, type LedgerRow,
} from '../lib/invoices'
import { moneyLocale } from '../lib/money'
import { buildTree } from '../lib/tree'
import type { CostContract, CostControlView, CostExpense, CostInvoice } from '../types'
import ExpenseDialog from './ExpenseDialog'
import ExpenseSheet from './ExpenseSheet'
import InvoiceDialog from './InvoiceDialog'
import InvoicesTabSkeleton from './InvoicesTabSkeleton'
import InvoicesToolbar from './InvoicesToolbar'
import LedgerTable from './LedgerTable'

interface Props { projectId: string }

const InvoicesTab: React.FC<Props> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { history, location } = useRouter()
  const actions = useModuleActions('COST_CONTROL', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const invoiceAf = invoiceAffordances(actions, null)
  const expenseAf = expenseAffordances(actions)
  const [filter, setFilter] = useState<LedgerFilterState>(EMPTY_LEDGER_FILTER)
  const [search] = useDebounce(filter.search, 300)
  const queries = ledgerQueries(filter, search)
  const invoicesQuery = useQuery<{ costInvoices: CostInvoice[] }>(Query.COST_INVOICES, {
    variables: { projectId, filter: queries.invoices ?? {} },
    skip: !queries.invoices,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const expensesQuery = useQuery<{ costExpenses: CostExpense[] }>(Query.COST_EXPENSES, {
    variables: { projectId, filter: queries.expenses ?? {} },
    skip: !queries.expenses,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: contractData } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // The sheet's reloads can be refused when someone deleted the expense; the sheet's own query never hears of it.
  const [lost, setLost] = useState<{ id: string; message: string } | null>(null)
  const onDetailError = useCallback((id: string, e: unknown) => {
    const message = (e as { message?: string } | null)?.message ?? ''
    if (isRecordGoneError(message, 'CostControl_error_expense_not_found')) setLost({ id, message })
  }, [])
  const listReloads: Reload[] = queries.expenses ? [{ query: Query.COST_EXPENSES, variables: { projectId, filter: queries.expenses } }] : []
  const expenseMutations = useExpenseMutations(projectId, listReloads, onDetailError)
  const invoiceMutations = useInvoiceMutations(projectId)
  const [creatingInvoice, setCreatingInvoice] = useState(false)
  const [creatingExpense, setCreatingExpense] = useState(false)
  const expenseParam = new URLSearchParams(location.search).get('expense')
  const [expenseId, setExpenseId] = useState<string | null>(expenseParam)
  useEffect(() => { if (expenseParam) setExpenseId(expenseParam) }, [expenseParam])

  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const currency = viewData?.costControl.settings.currency ?? 'CZK'
  const contracts = contractData?.costContracts ?? []
  const invoices = queries.invoices ? invoicesQuery.data?.costInvoices : []
  const expenses = queries.expenses ? expensesQuery.data?.costExpenses : []
  const rows = invoices && expenses ? ledgerRows(invoices, expenses) : undefined
  const error = invoicesQuery.error ?? expensesQuery.error
  const closeExpense = () => {
    setExpenseId(null)
    if (expenseParam) history.replace(`/project/${projectId}/cost-control?tab=invoices`)
  }
  const open = (row: LedgerRow) => {
    if (row.kind === 'INVOICE') history.push(`/project/${projectId}/cost-control/invoices/${row.id}`)
    else setExpenseId(row.id)
  }

  if (!rows && (invoicesQuery.loading || expensesQuery.loading)) return <InvoicesTabSkeleton />

  return (
    <div className="space-y-2">
      <InvoicesToolbar
        count={rows?.length ?? 0}
        filter={filter}
        onFilterChange={setFilter}
        contracts={invoiceContractOptions(contracts)}
        canCreateInvoice={invoiceAf.create}
        canCreateExpense={expenseAf.create}
        onCreateInvoice={() => setCreatingInvoice(true)}
        onCreateExpense={() => setCreatingExpense(true)}
      />
      {!rows && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
      {rows && rows.length === 0 && (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          {t(isLedgerFiltered(filter) ? 'CostControl_ledger_empty_filtered' : 'CostControl_ledger_empty')}
        </div>
      )}
      {rows && rows.length > 0 && <LedgerTable rows={rows} currency={currency} locale={locale} onOpen={open} />}
      <InvoiceDialog
        open={creatingInvoice}
        mode="create"
        contracts={invoiceContractOptions(contracts)}
        currency={currency}
        onOpenChange={setCreatingInvoice}
        onSubmit={async (input) => {
          const res = await invoiceMutations.create(input)
          if (res.ok) history.push(`/project/${projectId}/cost-control/invoices/${res.data.createCostInvoice.id}`)
          return res.ok
        }}
      />
      <ExpenseDialog
        open={creatingExpense}
        mode="create"
        tree={tree}
        currency={currency}
        onOpenChange={setCreatingExpense}
        onSubmit={async (input) => (await expenseMutations.create(input)).ok}
      />
      <ExpenseSheet
        expenseId={expenseId}
        projectId={projectId}
        tree={tree}
        currency={currency}
        locale={locale}
        af={expenseAf}
        canLinkDocuments={docControl.canView}
        mutations={expenseMutations}
        lostMessage={lost && lost.id === expenseId ? lost.message : null}
        onClose={closeExpense}
      />
    </div>
  )
}
export default InvoicesTab
